import { useEffect, useRef, useState } from 'react'
import { getGameweekForecasts, type ForecastsByFixture, type MatchForecast } from '../api/football'

/**
 * The gameweek's pre-match forecasts, fetched once.
 *
 * **One request for the whole gameweek**, and it fires on the gameweek changing and nothing else.
 * The picker cycles through every fixture, so opening one — or stepping a score, or toggling
 * Banker — must not go near the network: the forecast is a fixed pre-match number, and refetching
 * per interaction would put a paid provider call behind a user action one layer down.
 *
 * Failure is silent and empty. A modal with no bar is complete; a modal that complains about a
 * missing probability is not.
 */
export interface ForecastsController {
  /** Keyed by fixture id. A fixture with no stored forecast is absent, never zeroed. */
  byFixture: ForecastsByFixture
}

/** What was fetched, and which gameweek it describes — the pair has to move together. */
interface Loaded {
  gameweek: number
  byFixture: ForecastsByFixture
}

export function useForecasts(gameweek: number, ready: boolean): ForecastsController {
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  /** The ETag, and the gameweek it belongs to — carrying one across would 304 the new gameweek
   *  against the old one's rows. */
  const etag = useRef<{ gameweek: number; value: string | null } | null>(null)

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    const previous = etag.current?.gameweek === gameweek ? etag.current.value : null
    void (async () => {
      try {
        const response = await getGameweekForecasts(gameweek, previous)
        if (cancelled) return
        etag.current = { gameweek, value: response.etag }
        if (response.forecasts) setLoaded({ gameweek, byFixture: response.forecasts })
      } catch {
        if (!cancelled) setLoaded({ gameweek, byFixture: {} })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [gameweek, ready])

  // Keyed on the gameweek rather than cleared in the effect: a stale gameweek's forecasts must
  // never be handed out against the new one, not even for the render between the two.
  return { byFixture: loaded?.gameweek === gameweek ? loaded.byFixture : {} }
}

export type { MatchForecast }
